const roundMoney = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? Number(number.toFixed(2)) : 0;
};

const countryCode = (profile = {}) =>
  String(profile?.country_code || profile?.countryCode || "IN").trim().toUpperCase() || "IN";

export const resolveTaxRegimeFromProfile = (profile = {}) => {
  const explicit = String(profile?.tax_regime || profile?.taxRegime || "").trim().toLowerCase();
  if (explicit) return explicit;
  const country = countryCode(profile);
  if (country === "IN") return "gst_in";
  if (country === "GB" || country === "UK") return "vat_uk";
  if (country === "AE" || country === "UAE") return "vat_ae";
  if (country === "US") return "sales_tax_us_manual";
  return "simple_tax";
};

export const resolveTaxContractFromProfile = (profile = {}) =>
  countryCode(profile) === "IN" && resolveTaxRegimeFromProfile(profile) === "gst_in"
    ? "legacy_gst_v1"
    : "generic_tax_v2";

export const resolveTaxLabelFromProfile = (profile = {}, fallback = "Tax") => {
  const country = countryCode(profile);
  const regime = resolveTaxRegimeFromProfile(profile);
  if (regime.includes("sales") || regime.includes("use_tax") || country === "US") return "Sales Tax";
  if (regime.includes("vat") || ["GB", "UK", "AE", "UAE"].includes(country)) return "VAT";
  if (regime.includes("gst") || country === "IN") return "GST";
  return String(fallback || "Tax").trim() || "Tax";
};

export const resolveTaxRegistrationLabelFromProfile = (profile = {}) => {
  const country = countryCode(profile);
  const regime = resolveTaxRegimeFromProfile(profile);
  if (regime.includes("gst") || country === "IN") return "GST No.";
  if (country === "AE" || country === "UAE") return "TRN";
  if (regime.includes("vat") || country === "GB" || country === "UK") return "VAT No.";
  return "Tax ID";
};

export const buildTaxDetailsFromCartLines = ({
  userData,
  cartLines,
  grandTotal,
  deliveryCharge = 0,
} = {}) => {
  const taxLabel = resolveTaxLabelFromProfile(userData);
  const contract = resolveTaxContractFromProfile(userData);
  const split = taxLabel.toUpperCase() === "GST";
  const lines = (Array.isArray(cartLines) ? cartLines : [])
    .map((line, index) => {
      const quantity = Number(line?.quantity || line?.qty || 0);
      const unitBase = roundMoney(line?.priceaftertax ?? line?.price ?? 0);
      const unitWithTax = roundMoney(line?.taxedprice ?? line?.priceaftertax ?? line?.price ?? 0);
      const explicitTax = roundMoney(line?.taxamount);
      const unitTax = explicitTax > 0 ? explicitTax : roundMoney(Math.max(0, unitWithTax - unitBase));
      const base = roundMoney(unitBase * quantity);
      const amount = roundMoney(unitTax * quantity);
      const explicitCgst = roundMoney(line?.cgst);
      const explicitSgst = roundMoney(line?.sgst);
      const cgst = split ? roundMoney(explicitCgst > 0 ? explicitCgst * quantity : amount / 2) : 0;
      const sgst = split ? roundMoney(explicitSgst > 0 ? explicitSgst * quantity : amount - cgst) : 0;
      return {
        id: index + 1,
        name: taxLabel,
        rate: base > 0 ? roundMoney((amount / base) * 100) : 0,
        amount,
        base,
        taxableamount: base,
        cgst,
        sgst,
        igst: split ? 0 : amount,
        total: amount,
      };
    })
    .filter((line) => line.amount > 0 || line.base > 0);
  const subtotal = roundMoney(lines.reduce((sum, line) => sum + line.base, 0));
  const taxTotal = roundMoney(lines.reduce((sum, line) => sum + line.amount, 0));
  const delivery = roundMoney(deliveryCharge);
  return {
    source: `client_${contract}`,
    contract_version: contract,
    currency_code: String(userData?.currency_code || userData?.currencyCode || "INR").toUpperCase(),
    tax_regime: resolveTaxRegimeFromProfile(userData),
    tax_label: taxLabel,
    breakup_mode: split ? "split" : "single",
    subtotal,
    tax_total: taxTotal,
    delivery_charge: delivery,
    grand_total: grandTotal == null ? roundMoney(subtotal + taxTotal + delivery) : roundMoney(grandTotal),
    lines,
  };
};

export const normalizeTaxDetailsForDisplay = (details, fallback = "Tax") => {
  const label = String(details?.tax_label || fallback || "Tax").trim() || "Tax";
  const split = String(details?.breakup_mode || "").toLowerCase() === "split" || label.toUpperCase() === "GST";
  const rows = (Array.isArray(details?.lines) ? details.lines : []).map((line, index) => ({
    key: `tax-${line?.id ?? index + 1}`,
    taxable: roundMoney(line?.taxableamount ?? line?.base),
    rate: roundMoney(line?.rate ?? line?.percent),
    cgst: split ? roundMoney(line?.cgst) : 0,
    sgst: split ? roundMoney(line?.sgst) : 0,
    singleTax: split ? 0 : roundMoney(line?.igst ?? line?.amount ?? line?.total),
    total: roundMoney(line?.total ?? line?.amount),
  }));
  return {
    taxLabel: label,
    breakupMode: split ? "split" : "single",
    splitTaxBreakup: split,
    rows,
    taxTotal: roundMoney(details?.tax_total ?? rows.reduce((sum, row) => sum + row.total, 0)),
  };
};
